/**
 * SimilarityNavigation Component
 * ==============================
 * PRIMARY NAVIGATION SYSTEM
 * 
 * 3 Groups based on DESIGN INTENT:
 * - Similar Materials: same visual language
 * - Strong Alternatives: same function, different expression
 * - Contrast Alternatives: intentional design tension
 * 
 * Uses unified SRE logic - NO duplicated logic
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBlueprint } from '../../context/BlueprintContext';
import { getSlabRealityAPI } from '../../services/slabRealityApi';
import { getSimilarityNavigation, getSimilarityGroupLabels, formatScore } from '../../utils/sreEngine';
import { getMaterialImageUrl, handleImageError } from '../../utils/materialImages';
import { 
  RefreshCw, 
  ChevronRight, 
  Layers, 
  Shuffle, 
  Contrast,
  ArrowRight,
  Zap
} from 'lucide-react';

// Group icons mapping
const GROUP_ICONS = {
  similar: Layers,
  strongAlternatives: Shuffle,
  contrastAlternatives: Contrast
};

// Score badge - minimal
const ScoreBadge = ({ score }) => (
  <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5">
    <Zap size={10} className="text-[#D6C1A5]" />
    <span className="text-xs font-medium text-white">{score}%</span>
  </div>
);

// Material card for similarity navigation
const SimilarityMaterialCard = ({ item, buildRoute }) => {
  const { material, score, label, reasons } = item;
  const materialLink = buildRoute('material_detail', { slug: material.id });
  
  return (
    <Link
      to={materialLink}
      className="group flex gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-all"
      data-testid={`similarity-card-${material.id}`}
    >
      {/* Image */}
      <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
        <img
          src={getMaterialImageUrl(material)}
          alt={material.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={handleImageError}
        />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4 className="font-medium text-white truncate group-hover:text-[#D6C1A5] transition-colors">
            {material.name}
          </h4>
          <ScoreBadge score={score} />
        </div>
        
        {/* Category & Color */}
        <div className="flex items-center gap-2 text-xs text-white/40 mb-2">
          {material.category && <span className="capitalize">{material.category}</span>}
          {material.category && material.color_family && <span>•</span>}
          {material.color_family && <span className="capitalize">{material.color_family}</span>}
        </div>
        
        {/* First reason */}
        {reasons?.[0] && (
          <p className="text-[11px] text-white/50 flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-[#D6C1A5]" />
            {reasons[0].explanation}
          </p>
        )}
      </div>
      
      {/* Arrow */}
      <ChevronRight size={16} className="text-white/20 group-hover:text-[#D6C1A5] group-hover:translate-x-1 transition-all flex-shrink-0 self-center" />
    </Link>
  );
};

// Similarity group section
const SimilarityGroup = ({ 
  groupKey, 
  items, 
  title, 
  description, 
  icon: Icon,
  buildRoute,
  sourceMaterialId,
  getRoute
}) => {
  if (!items?.length) return null;
  
  return (
    <div className="space-y-4" data-testid={`similarity-group-${groupKey}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-[#D6C1A5]/10 flex items-center justify-center">
            <Icon size={16} className="text-[#D6C1A5]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{title}</h3>
            <p className="text-[11px] text-white/40">{description}</p>
          </div>
        </div>
        
        <Link
          to={`${getRoute('explorer', '/materials')}?intent=${groupKey}&from=${sourceMaterialId}`}
          className="text-xs text-[#D6C1A5] hover:text-white flex items-center gap-1 transition-colors"
        >
          Tutti
          <ArrowRight size={12} />
        </Link>
      </div>
      
      {/* Cards */}
      <div className="space-y-2">
        {items.map((item) => (
          <SimilarityMaterialCard 
            key={item.material.id} 
            item={item} 
            buildRoute={buildRoute}
          />
        ))}
      </div>
    </div>
  );
};

const SimilarityNavigation = ({ 
  sourceMaterial,
  limits = { similar: 3, strongAlternatives: 3, contrastAlternatives: 3 },
  compact = false
}) => {
  const { content, logic, buildRoute, getRoute } = useBlueprint();
  
  // State
  const [allMaterials, setAllMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState('similar');
  
  // Get group labels from config
  const groupLabels = useMemo(() => getSimilarityGroupLabels({ content }), [content]);
  
  // Fetch all materials
  useEffect(() => {
    const fetchMaterials = async () => {
      setLoading(true);
      try {
        const result = await getSlabRealityAPI();
        if (result.success && result.data?.materials) {
          setAllMaterials(result.data.materials);
        }
      } catch (err) {
        console.error('SimilarityNavigation fetch error:', err);
      }
      setLoading(false);
    };
    
    fetchMaterials();
  }, []);
  
  // Calculate similarity groups
  const groups = useMemo(() => {
    if (!sourceMaterial || !allMaterials.length) {
      return { similar: [], strongAlternatives: [], contrastAlternatives: [] };
    }
    
    return getSimilarityNavigation(sourceMaterial, allMaterials, { logic }, limits);
  }, [sourceMaterial, allMaterials, logic, limits]);
  
  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw size={20} className="text-[#D6C1A5] animate-spin" />
      </div>
    );
  }
  
  // Check if any group has items
  const hasAnyResults = groups.similar.length > 0 || 
                        groups.strongAlternatives.length > 0 || 
                        groups.contrastAlternatives.length > 0;
  
  if (!hasAnyResults) {
    return (
      <div className="text-center py-8">
        <p className="text-white/40 text-sm">Nessuna alternativa trovata</p>
      </div>
    );
  }
  
  // Compact mode - tabs + single group
  if (compact) {
    const groupKeys = ['similar', 'strongAlternatives', 'contrastAlternatives'];
    const activeItems = groups[activeGroup] || [];
    const activeLabel = groupLabels[activeGroup];
    const ActiveIcon = GROUP_ICONS[activeGroup];
    
    return (
      <div className="space-y-4" data-testid="similarity-navigation-compact">
        {/* Tabs */}
        <div className="flex gap-2 p-1 bg-white/5 rounded">
          {groupKeys.map((key) => {
            const count = groups[key]?.length || 0;
            if (count === 0) return null;
            
            const Icon = GROUP_ICONS[key];
            const isActive = activeGroup === key;
            
            return (
              <button
                key={key}
                onClick={() => setActiveGroup(key)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 text-xs font-medium transition-colors ${
                  isActive 
                    ? 'bg-[#D6C1A5] text-[#0A0A09]' 
                    : 'text-white/50 hover:text-white'
                }`}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{groupLabels[key]?.title?.split(' ')[0]}</span>
                <span className="text-[10px] opacity-60">({count})</span>
              </button>
            );
          })}
        </div>
        
        {/* Active Group Content */}
        <SimilarityGroup
          groupKey={activeGroup}
          items={activeItems}
          title={activeLabel?.title}
          description={activeLabel?.description}
          icon={ActiveIcon}
          buildRoute={buildRoute}
          sourceMaterialId={sourceMaterial?.id}
          getRoute={getRoute}
        />
      </div>
    );
  }
  
  // Full mode - all 3 groups stacked
  return (
    <div className="space-y-10" data-testid="similarity-navigation">
      {/* Similar Materials */}
      {groups.similar.length > 0 && (
        <SimilarityGroup
          groupKey="similar"
          items={groups.similar}
          title={groupLabels.similar.title}
          description={groupLabels.similar.description}
          icon={GROUP_ICONS.similar}
          buildRoute={buildRoute}
          sourceMaterialId={sourceMaterial?.id}
          getRoute={getRoute}
        />
      )}
      
      {/* Strong Alternatives */}
      {groups.strongAlternatives.length > 0 && (
        <SimilarityGroup
          groupKey="strongAlternatives"
          items={groups.strongAlternatives}
          title={groupLabels.strongAlternatives.title}
          description={groupLabels.strongAlternatives.description}
          icon={GROUP_ICONS.strongAlternatives}
          buildRoute={buildRoute}
          sourceMaterialId={sourceMaterial?.id}
          getRoute={getRoute}
        />
      )}
      
      {/* Contrast Alternatives */}
      {groups.contrastAlternatives.length > 0 && (
        <SimilarityGroup
          groupKey="contrastAlternatives"
          items={groups.contrastAlternatives}
          title={groupLabels.contrastAlternatives.title}
          description={groupLabels.contrastAlternatives.description}
          icon={GROUP_ICONS.contrastAlternatives}
          buildRoute={buildRoute}
          sourceMaterialId={sourceMaterial?.id}
          getRoute={getRoute}
        />
      )}
    </div>
  );
};

export default SimilarityNavigation;
